import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import PortalLayout from './PortalLayout';
import { useAuth } from '../../context/AuthContext';
import { canAccess } from '../../constants/tiers';
import { toast } from 'sonner';
import {
  ChevronLeft, Lock, Loader2, Send, MessageSquare, UserPlus, Check, X, Inbox as InboxIcon,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const POLL_MS = 5000;

/**
 * Peer Circles → Inbox (iter 75 Phase 2).
 *
 * Two tabs: Conversations and Requests.
 * Polls /api/peer-circles/conversations + /api/peer-circles/inbox/unread-count
 * every 5s. When a conversation is open, also polls its message thread.
 *
 * URL params we honour:
 *   ?tab=requests       → open the Requests tab on mount
 *   ?to={recipient_id}  → open a thread with that user (creates conv on first send)
 */

const InitialBubble = ({ name, size = 40 }) => {
  const initial = (name || '?').trim()[0]?.toUpperCase() || '?';
  return (
    <div
      className="rounded-full bg-[#2CA8A0] text-white font-semibold flex items-center justify-center shrink-0"
      style={{ width: size, height: size, fontSize: size / 2.5 }}
    >
      {initial}
    </div>
  );
};

const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const apiGet = async (path) => {
  const res = await fetch(`${API_URL}${path}`, {
    credentials: 'include',
    headers: authHeaders(),
  });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.detail?.message || d.detail || `GET ${path} failed`);
  }
  return res.json();
};

const apiSend = async (path, method, body) => {
  const res = await fetch(`${API_URL}${path}`, {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.detail?.message || d.detail || `${method} ${path} failed`);
  }
  return res.json();
};

const TabButton = ({ active, onClick, children, badge, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`relative px-4 py-2 rounded-full font-mono uppercase text-[11px] tracking-[0.15em] transition ${
      active
        ? 'bg-[#0A1F2E] text-white'
        : 'bg-white border border-[#EFE6CF] text-[#0A1F2E] hover:border-[#0A1F2E]/30'
    }`}
  >
    {children}
    {badge > 0 && (
      <span className="ml-2 inline-flex items-center justify-center bg-[#E8A830] text-[#0A1F2E] font-bold rounded-full text-[10px] min-w-[18px] h-[18px] px-1">
        {badge}
      </span>
    )}
  </button>
);

const RequestsTab = ({ onChanged }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const d = await apiGet('/api/peer-circles/connections/requests');
      setItems(d.requests || []);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const respond = async (connId, action) => {
    setBusyId(connId);
    try {
      await apiSend(`/api/peer-circles/connections/${connId}`, 'PATCH', { action });
      setItems((cur) => cur.filter((r) => r.connection_id !== connId));
      toast.success(action === 'accept' ? 'Connection accepted.' : 'Request declined.');
      onChanged?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-7 h-7 text-[#2CA8A0] animate-spin" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="bg-white border border-[#EFE6CF] rounded-2xl p-12 text-center" data-testid="requests-empty">
        <UserPlus className="w-10 h-10 text-[#1A5C6E]/40 mx-auto mb-3" />
        <p className="text-[#0A1F2E]/65">No pending requests right now.</p>
      </div>
    );
  }

  return (
    <ul className="space-y-3" data-testid="requests-list">
      {items.map((r) => {
        const u = r.requester;
        const name = `${u.first_name}${u.last_name_initial ? ` ${u.last_name_initial}.` : ''}`;
        const from = [u.from_city, u.from_country].filter(Boolean).join(', ');
        return (
          <li
            key={r.connection_id}
            className="bg-white border border-[#EFE6CF] rounded-2xl p-4 sm:p-5 flex items-center gap-4"
            data-testid={`request-row-${r.connection_id}`}
          >
            <Link to={`/portal/members/${u.user_id}`}>
              {u.profile_photo_url ? (
                <img src={u.profile_photo_url} alt="" className="w-12 h-12 rounded-full object-cover ring-2 ring-[#EFE6CF]" />
              ) : (
                <InitialBubble name={u.first_name} size={48} />
              )}
            </Link>
            <div className="flex-1 min-w-0">
              <Link to={`/portal/members/${u.user_id}`} className="font-display italic text-lg text-[#0A1F2E] hover:text-[#1A5C6E] block truncate">
                {name}
              </Link>
              {(from || u.current_city) && (
                <p className="text-xs text-[#0A1F2E]/55 truncate">
                  {from && <span>From {from}</span>}
                  {from && u.current_city && <span> · </span>}
                  {u.current_city && <span>In {u.current_city}</span>}
                </p>
              )}
              {u.bio && <p className="text-xs text-[#0A1F2E]/65 mt-1 line-clamp-1">{u.bio}</p>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                disabled={busyId === r.connection_id}
                onClick={() => respond(r.connection_id, 'accept')}
                className="inline-flex items-center gap-1 bg-[#0A1F2E] hover:bg-[#163448] disabled:opacity-50 text-white font-mono uppercase text-[10px] tracking-[0.15em] px-3 py-2 rounded-full transition"
                data-testid={`request-accept-${r.connection_id}`}
              >
                <Check className="w-3.5 h-3.5" /> Accept
              </button>
              <button
                type="button"
                disabled={busyId === r.connection_id}
                onClick={() => respond(r.connection_id, 'decline')}
                className="inline-flex items-center gap-1 bg-white border border-[#0A1F2E]/20 hover:border-[#B91C1C]/50 hover:text-[#B91C1C] disabled:opacity-50 text-[#0A1F2E] font-mono uppercase text-[10px] tracking-[0.15em] px-3 py-2 rounded-full transition"
                data-testid={`request-decline-${r.connection_id}`}
              >
                <X className="w-3.5 h-3.5" /> Decline
              </button>
            </div>
          </li>
        );
      })}
    </ul>
  );
};

const ConversationList = ({ conversations, activeId, onSelect }) => {
  if (conversations.length === 0) {
    return (
      <div className="p-6 text-center text-[#0A1F2E]/55 text-sm" data-testid="conversations-empty">
        No conversations yet. Connect with members from the directory to start.
      </div>
    );
  }
  return (
    <ul className="divide-y divide-[#EFE6CF]" data-testid="conversations-list">
      {conversations.map((c) => {
        const other = c.other || {};
        const name = `${other.first_name || 'Member'}${other.last_name_initial ? ` ${other.last_name_initial}.` : ''}`;
        const isActive = activeId === c.conversation_id;
        return (
          <li
            key={c.conversation_id}
            onClick={() => onSelect(c.conversation_id, other.user_id)}
            className={`p-4 cursor-pointer transition ${isActive ? 'bg-[#F4ECD8]' : 'hover:bg-[#F9F6EF]'}`}
            data-testid={`conversation-row-${c.conversation_id}`}
          >
            <div className="flex items-center gap-3">
              {other.profile_photo_url ? (
                <img src={other.profile_photo_url} alt="" className="w-10 h-10 rounded-full object-cover ring-2 ring-[#EFE6CF]" />
              ) : (
                <InitialBubble name={other.first_name} size={40} />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-display italic text-[#0A1F2E] truncate">{name}</p>
                  {c.unread_count > 0 && (
                    <span
                      className="bg-[#E8A830] text-[#0A1F2E] font-bold rounded-full text-[10px] min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center"
                      data-testid={`conversation-unread-${c.conversation_id}`}
                    >
                      {c.unread_count}
                    </span>
                  )}
                </div>
                <p className="text-xs text-[#0A1F2E]/55 truncate">
                  {c.last_message_preview || 'No messages yet'}
                </p>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
};

const ChatThread = ({ conversationId, recipientId, viewerId, onAfterSend }) => {
  const [messages, setMessages] = useState([]);
  const [other, setOther] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const fetchThread = useCallback(async () => {
    if (!conversationId) {
      // Brand-new conversation - no thread yet. Load just the other user's name.
      if (recipientId) {
        try {
          const card = await apiGet(`/api/members/${recipientId}`);
          setOther({
            user_id: card.user_id,
            first_name: card.first_name,
            last_name_initial: card.last_name_initial,
            profile_photo_url: card.profile_photo_url,
          });
        } catch { /* noop */ }
      }
      setMessages([]);
      setLoading(false);
      return;
    }
    try {
      const d = await apiGet(`/api/peer-circles/conversations/${conversationId}/messages`);
      setMessages(d.messages || []);
      setOther(d.other || null);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [conversationId, recipientId]);

  useEffect(() => { setLoading(true); fetchThread(); }, [fetchThread]);

  // Poll for new messages every POLL_MS while a thread is open.
  useEffect(() => {
    if (!conversationId) return undefined;
    const t = setInterval(fetchThread, POLL_MS);
    return () => clearInterval(t);
  }, [conversationId, fetchThread]);

  // Autoscroll to bottom on new messages.
  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages.length]);

  const send = async (e) => {
    e?.preventDefault?.();
    const text = draft.trim();
    if (!text || sending) return;
    const targetId = recipientId || other?.user_id;
    if (!targetId) {
      toast.error('Pick a recipient first.');
      return;
    }
    setSending(true);
    try {
      const r = await apiSend('/api/peer-circles/messages/send', 'POST', {
        recipient_id: targetId,
        body: text,
      });
      setDraft('');
      // Optimistic append + fetch fresh
      setMessages((cur) => [...cur, r.message]);
      onAfterSend?.(r.conversation_id);
    } catch (e2) {
      toast.error(e2.message);
    } finally {
      setSending(false);
    }
  };

  if (!conversationId && !recipientId) {
    return (
      <div className="hidden md:flex items-center justify-center h-full text-[#0A1F2E]/55 text-sm p-12 text-center" data-testid="thread-empty-state">
        Select a conversation to start chatting.
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="chat-thread">
      <header className="border-b border-[#EFE6CF] px-5 py-3 flex items-center gap-3 bg-white/60">
        {other?.profile_photo_url ? (
          <img src={other.profile_photo_url} alt="" className="w-9 h-9 rounded-full object-cover ring-2 ring-[#EFE6CF]" />
        ) : (
          <InitialBubble name={other?.first_name} size={36} />
        )}
        <p className="font-display italic text-lg text-[#0A1F2E] truncate" data-testid="chat-thread-name">
          {other ? `${other.first_name}${other.last_name_initial ? ` ${other.last_name_initial}.` : ''}` : 'Conversation'}
        </p>
      </header>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2 bg-[#F9F6EF]">
        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-[#2CA8A0] animate-spin" />
          </div>
        )}
        {!loading && messages.length === 0 && (
          <p className="text-center text-[#0A1F2E]/55 text-sm py-8">Say hi - this conversation is empty.</p>
        )}
        {!loading && messages.map((m) => {
          const mine = m.sender_id === viewerId;
          return (
            <div
              key={m.id}
              className={`flex ${mine ? 'justify-end' : 'justify-start'}`}
              data-testid={`chat-message-${m.id}`}
            >
              <div className={`max-w-[78%] rounded-2xl px-4 py-2 text-sm leading-snug break-words ${
                mine ? 'bg-[#0A1F2E] text-white' : 'bg-white text-[#0A1F2E] border border-[#EFE6CF]'
              }`}>
                {m.body}
                <p className={`text-[10px] mt-1 ${mine ? 'text-white/55' : 'text-[#0A1F2E]/45'}`}>
                  {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: 'America/Chicago'})}
                </p>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={send} className="border-t border-[#EFE6CF] p-3 flex items-center gap-2 bg-white" data-testid="chat-input-bar">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Type a message…"
          className="flex-1 bg-[#F9F6EF] border border-[#EFE6CF] text-[#0A1F2E] rounded-full px-4 py-2.5 text-sm focus:outline-none focus:border-[#2CA8A0] focus:ring-2 focus:ring-[#2CA8A0]/20"
          data-testid="chat-input"
        />
        <button
          type="submit"
          disabled={sending || !draft.trim()}
          className="bg-[#0A1F2E] hover:bg-[#163448] disabled:opacity-50 text-white rounded-full p-2.5 transition"
          data-testid="chat-send-btn"
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
};

const PortalPeerCirclesInbox = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const unlocked = canAccess(user?.role, 'peerCircles');

  const initialTab = searchParams.get('tab') === 'requests' ? 'requests' : 'messages';
  const initialTo = searchParams.get('to') || null;

  const [tab, setTab] = useState(initialTab);
  const [conversations, setConversations] = useState([]);
  const [unread, setUnread] = useState({ unread_messages: 0, pending_requests: 0, total: 0 });
  const [activeConvId, setActiveConvId] = useState(null);
  const [activeRecipientId, setActiveRecipientId] = useState(initialTo);
  const [loaded, setLoaded] = useState(false);

  const fetchConversations = useCallback(async () => {
    try {
      const d = await apiGet('/api/peer-circles/conversations');
      setConversations(d.conversations || []);
    } catch (e) {
      console.error('inbox conversations:', e.message);
    }
  }, []);

  const fetchUnread = useCallback(async () => {
    try {
      const d = await apiGet('/api/peer-circles/inbox/unread-count');
      setUnread(d);
    } catch { /* noop */ }
  }, []);

  // Initial load - both conversations and unread
  useEffect(() => {
    if (!unlocked) return;
    Promise.all([fetchConversations(), fetchUnread()]).finally(() => setLoaded(true));
  }, [unlocked, fetchConversations, fetchUnread]);

  // Resolve ?to=... into an active conversation on mount
  useEffect(() => {
    if (!initialTo || !conversations.length) return;
    const existing = conversations.find((c) => c.other?.user_id === initialTo);
    if (existing) setActiveConvId(existing.conversation_id);
  }, [initialTo, conversations]);

  // Poll every 5s for fresh inbox state
  useEffect(() => {
    if (!unlocked) return undefined;
    const t = setInterval(() => {
      fetchConversations();
      fetchUnread();
    }, POLL_MS);
    return () => clearInterval(t);
  }, [unlocked, fetchConversations, fetchUnread]);

  const switchTab = (t) => {
    setTab(t);
    setSearchParams(t === 'requests' ? { tab: 'requests' } : {});
  };

  const handleSelect = (convId, recipId) => {
    setActiveConvId(convId);
    setActiveRecipientId(recipId);
  };

  const handleAfterSend = (convId) => {
    if (!activeConvId && convId) setActiveConvId(convId);
    fetchConversations();
  };

  if (!unlocked) {
    return (
      <PortalLayout activeTab="peerCircles">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-12 py-14" data-testid="inbox-locked">
          <div className="bg-white border border-[#EFE6CF] rounded-2xl p-8 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-[#F4ECD8] flex items-center justify-center mb-4">
              <Lock className="w-6 h-6 text-[#1A5C6E]" />
            </div>
            <h1 className="font-display italic text-3xl text-[#0A1F2E] mb-2">Inbox is part of Peer Circles</h1>
            <p className="text-[#0A1F2E]/70 mb-6">Unlock DMs and connect requests with a Community or Connected plan.</p>
            <Link
              to="/portal/upgrade"
              className="inline-flex items-center gap-1 bg-[#0A1F2E] hover:bg-[#163448] text-white font-mono uppercase text-[11px] tracking-[0.15em] px-6 py-3 rounded-full transition"
              data-testid="inbox-upgrade-cta"
            >
              See plans
            </Link>
          </div>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout activeTab="peerCircles">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-8 md:py-10" data-testid="peer-circles-inbox">
        <button
          type="button"
          onClick={() => navigate('/portal/peer-circles')}
          className="inline-flex items-center gap-1 text-[#1A5C6E] hover:text-[#0A1F2E] text-sm mb-6 transition font-mono uppercase tracking-[0.12em] text-[11px]"
          data-testid="inbox-back-to-hub"
        >
          <ChevronLeft className="w-4 h-4" /> Back to Peer Circles
        </button>

        <header className="mb-6">
          <p className="font-mono uppercase text-[10px] tracking-[0.2em] text-[#1A5C6E] mb-2">
            Peer Circles · Inbox
          </p>
          <h1 className="font-display italic text-4xl md:text-5xl text-[#0A1F2E] leading-[1.05]">
            Your conversations.
          </h1>
        </header>

        <div className="flex gap-2 mb-5">
          <TabButton
            active={tab === 'messages'}
            onClick={() => switchTab('messages')}
            badge={unread.unread_messages}
            testId="inbox-tab-messages"
          >
            <span className="inline-flex items-center gap-1.5"><MessageSquare className="w-3 h-3" /> Messages</span>
          </TabButton>
          <TabButton
            active={tab === 'requests'}
            onClick={() => switchTab('requests')}
            badge={unread.pending_requests}
            testId="inbox-tab-requests"
          >
            <span className="inline-flex items-center gap-1.5"><UserPlus className="w-3 h-3" /> Requests</span>
          </TabButton>
        </div>

        {!loaded ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-7 h-7 text-[#2CA8A0] animate-spin" />
          </div>
        ) : tab === 'requests' ? (
          <RequestsTab onChanged={() => { fetchConversations(); fetchUnread(); }} />
        ) : (
          <div
            className="bg-white border border-[#EFE6CF] rounded-2xl overflow-hidden grid grid-cols-1 md:grid-cols-[320px_1fr]"
            style={{ minHeight: 520 }}
            data-testid="inbox-pane"
          >
            <aside className="border-b md:border-b-0 md:border-r border-[#EFE6CF] bg-white overflow-y-auto" style={{ maxHeight: 600 }}>
              <div className="p-4 border-b border-[#EFE6CF] flex items-center gap-2">
                <InboxIcon className="w-4 h-4 text-[#1A5C6E]" />
                <p className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/55">Conversations</p>
              </div>
              <ConversationList
                conversations={conversations}
                activeId={activeConvId}
                onSelect={handleSelect}
              />
            </aside>
            <section className="bg-[#F9F6EF]" style={{ minHeight: 520 }}>
              <ChatThread
                conversationId={activeConvId}
                recipientId={activeConvId ? null : activeRecipientId}
                viewerId={user?.id}
                onAfterSend={handleAfterSend}
              />
            </section>
          </div>
        )}
      </div>
    </PortalLayout>
  );
};

export default PortalPeerCirclesInbox;
